import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Target, Dumbbell, TrendingUp, Link2, ArrowUpCircle, Trophy, Zap, Star, Sparkles, Play, X, ChevronLeft } from "lucide-react";

// 5 lb increments from -20 to +20 — applies uniformly to every strength
// exercise (per user direction "let's just do the 5 lb jumps").
const DELTA_STOPS = [-20, -15, -10, -5, 0, 5, 10, 15, 20];

const formatDelta = (v) => {
  if (v === 0) return "Same";
  const sign = v > 0 ? "+" : "−";
  return `${sign}${Math.abs(v)} lb`;
};

// ─── iOS-style wheel picker ───
// Vertical scroll-snap list with the centered row highlighted (like the
// alarm/timer wheels in iOS). Center row = current value. Tap a row to snap
// to it; native scroll/wheel/touch gestures also work and snap automatically.
const ITEM_H = 40;
const VISIBLE_ROWS = 5; // 2 above center, center, 2 below

const WheelPicker = ({ stops, value, onChange, testIdPrefix }) => {
  const ref = useRef(null);
  const scrollEndTimer = useRef(null);
  const PAD = ((VISIBLE_ROWS - 1) / 2) * ITEM_H;

  // Sync scroll position when `value` changes from the outside (e.g. initial
  // mount, Custom confirm). We avoid feedback loops below by checking that
  // the chosen value differs from the current one before calling onChange.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const idx = stops.indexOf(value);
    if (idx < 0) return;
    el.scrollTo({ top: idx * ITEM_H, behavior: "auto" });
  }, [value, stops]);

  const handleScroll = () => {
    clearTimeout(scrollEndTimer.current);
    scrollEndTimer.current = setTimeout(() => {
      const el = ref.current;
      if (!el) return;
      const idx = Math.round(el.scrollTop / ITEM_H);
      const clamped = Math.max(0, Math.min(stops.length - 1, idx));
      const newVal = stops[clamped];
      if (newVal !== value) onChange(newVal);
    }, 110);
  };

  const tapRow = (v) => {
    const el = ref.current;
    if (!el) return;
    const idx = stops.indexOf(v);
    if (idx < 0) return;
    el.scrollTo({ top: idx * ITEM_H, behavior: "smooth" });
  };

  return (
    <div className="relative bg-[#0B0F1A] rounded-xl overflow-hidden border border-[#1E293B]"
         style={{ height: ITEM_H * VISIBLE_ROWS }}
         data-testid={`${testIdPrefix}-wheel`}>
      {/* Center selection band */}
      <div className="absolute inset-x-0 pointer-events-none border-y-2 border-primary/50 bg-primary/[0.07]"
           style={{ top: PAD, height: ITEM_H }} />
      {/* Top fade */}
      <div className="absolute inset-x-0 top-0 pointer-events-none z-10"
           style={{ height: PAD,
                    background: "linear-gradient(to bottom, #0B0F1A 0%, rgba(11,15,26,0.6) 50%, transparent 100%)" }} />
      {/* Bottom fade */}
      <div className="absolute inset-x-0 bottom-0 pointer-events-none z-10"
           style={{ height: PAD,
                    background: "linear-gradient(to top, #0B0F1A 0%, rgba(11,15,26,0.6) 50%, transparent 100%)" }} />
      {/* Scroll list */}
      <div
        ref={ref}
        onScroll={handleScroll}
        className="h-full overflow-y-scroll"
        style={{
          scrollSnapType: "y mandatory",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          paddingTop: PAD,
          paddingBottom: PAD,
          WebkitOverflowScrolling: "touch",
        }}>
        <style>{`[data-testid="${testIdPrefix}-wheel"] > div:last-child::-webkit-scrollbar{display:none}`}</style>
        {stops.map(s => {
          const active = s === value;
          return (
            <div
              key={s}
              onClick={() => tapRow(s)}
              className="flex items-center justify-center cursor-pointer select-none transition-all"
              style={{
                height: ITEM_H,
                scrollSnapAlign: "start",
                color: active ? "#fff" : "rgba(255,255,255,0.32)",
                fontSize: active ? 22 : 16,
                fontWeight: active ? 800 : 500,
                letterSpacing: active ? "0.02em" : "0",
              }}
              data-testid={`${testIdPrefix}-row-${s}`}>
              {formatDelta(s)}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const LevelUpConfetti = () => {
  const colors = ["#F59E0B", "#FBBF24", "#FCD34D", "#D97706", "#10B981", "#3B82F6"];
  const [particles] = useState(() => Array.from({ length: 24 }, (_, i) => ({
    id: i, x: 10 + Math.random() * 80, delay: Math.random() * 0.4,
    color: colors[Math.floor(Math.random() * colors.length)], size: 3 + Math.random() * 5, drift: -20 + Math.random() * 40,
  })));
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden z-10">
      {particles.map(p => (
        <motion.div key={p.id} initial={{ y: "50%", x: `${p.x}%`, opacity: 1, scale: 0 }}
          animate={{ y: "-20%", x: `${p.x + p.drift}%`, opacity: 0, scale: 1, rotate: 180 + Math.random() * 360 }}
          transition={{ duration: 1.8 + Math.random() * 0.8, delay: p.delay, ease: "easeOut" }}
          style={{ position: "absolute", width: p.size, height: p.size, backgroundColor: p.color, borderRadius: Math.random() > 0.5 ? "50%" : "1px" }} />
      ))}
    </div>
  );
};

const LevelProgressionPanel = ({ progressionInfo: p, sectionIndex, exerciseIndex, onLevelUp }) => {
  const [leveling, setLeveling] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const handleLevelUp = async () => { setLeveling(true); try { await onLevelUp(p.slotName); setAccepted(true); } finally { setLeveling(false); } };

  if (p.criteriaMet && !accepted) return (
    <motion.div initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} className="rounded-xl overflow-hidden relative"
      style={{ background: "linear-gradient(135deg, rgba(217,119,6,0.15) 0%, rgba(245,158,11,0.08) 50%, rgba(16,185,129,0.1) 100%)", border: "1.5px solid rgba(245,158,11,0.5)" }}
      data-testid={`level-progression-${sectionIndex}-${exerciseIndex}`}>
      <LevelUpConfetti />
      <div className="relative z-20 p-4 space-y-3">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-amber-500/25 border border-amber-500/40 flex items-center justify-center"><Sparkles className="w-5 h-5 text-amber-400" /></div>
          <div><h4 className="text-base font-extrabold text-amber-300 uppercase">Level Up Unlocked</h4><p className="text-[11px] text-amber-400/70">Level {p.currentLevel} of {p.totalLevels}</p></div>
        </div>
        <p className="text-sm text-gray-200">You hit <span className="font-bold text-white">{p.currentMax} lbs</span> — past <span className="font-bold text-amber-400">{p.targetWeight} lb</span>!</p>
        <div className="bg-black/30 rounded-lg p-3 flex items-center gap-3">
          <div className="flex-1 text-center"><p className="text-[10px] text-gray-500 uppercase">Current</p><p className="text-sm text-gray-400 line-through">{p.currentExerciseName}</p></div>
          <ArrowUpCircle className="w-5 h-5 text-amber-400 rotate-90 flex-shrink-0" />
          <div className="flex-1 text-center"><p className="text-[10px] text-amber-400 uppercase">New</p><p className="text-sm text-white font-semibold">{p.nextLevelExerciseName}</p></div>
        </div>
        <button type="button" onClick={handleLevelUp} disabled={leveling}
          className="w-full py-3 rounded-lg font-bold text-sm text-black flex items-center justify-center gap-2 disabled:opacity-50"
          style={{ background: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)" }} data-testid={`level-up-btn-${sectionIndex}-${exerciseIndex}`}>
          {leveling ? <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-black" /> : <><Star className="w-4 h-4" /> Accept Level Up</>}
        </button>
      </div>
    </motion.div>
  );
  if (accepted) return (
    <div className="p-4 rounded-xl bg-emerald-500/15 border border-emerald-500/40 text-center" data-testid={`level-progression-${sectionIndex}-${exerciseIndex}`}>
      <Trophy className="w-6 h-6 text-amber-400 mx-auto mb-1" /><p className="text-sm font-bold text-emerald-300">Leveled Up!</p>
      <p className="text-xs text-emerald-400/70">Next session: {p.nextLevelExerciseName}</p>
    </div>
  );
  return (
    <div className="rounded-xl bg-[#0F1419] border border-[#1E293B] overflow-hidden" data-testid={`level-progression-${sectionIndex}-${exerciseIndex}`}>
      <div className="px-3 py-2 flex items-center justify-between bg-[#141B2D]">
        <div className="flex items-center gap-1.5"><Zap className="w-3.5 h-3.5 text-violet-400" /><span className="text-xs font-medium text-gray-400">Level {p.currentLevel}/{p.totalLevels}</span></div>
        {p.nextLevelExerciseName && <span className="text-[10px] text-gray-500">Next: {p.nextLevelExerciseName}</span>}
      </div>
      {p.targetWeight !== null && (
        <div className="px-3 py-2.5">
          <div className="flex items-center justify-between text-[11px] mb-1"><span className="text-gray-500">Target: {p.targetWeight} lbs</span><span className="text-white font-medium">{p.currentMax || 0} / {p.targetWeight}</span></div>
          <div className="w-full bg-[#2D3B4F] rounded-full h-1.5"><div className="h-1.5 rounded-full bg-violet-500 transition-all duration-700" style={{ width: `${Math.min(100, p.progressPercent)}%` }} /></div>
        </div>
      )}
    </div>
  );
};

const ExerciseCountdown = ({ onComplete }) => {
  const [count, setCount] = useState(3);
  useEffect(() => {
    if (count <= 0) { onComplete(); return; }
    const t = setTimeout(() => setCount(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [count, onComplete]);
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center justify-center py-12">
      <p className="text-sm text-white/50 mb-4">Next exercise in</p>
      <motion.span key={count} initial={{ scale: 1.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.3 }}
        className="text-6xl font-black text-blue-400">{count}</motion.span>
    </motion.div>
  );
};

// ─── SINGLE SET CARD ───
const SetCard = ({ setIndex, numSets, weight, reps, plates, rest, actualWeight, onActualChange, onComplete, equipLabel }) => (
  <motion.div
    key={setIndex}
    initial={{ opacity: 0, x: 80 }}
    animate={{ opacity: 1, x: 0 }}
    exit={{ opacity: 0, x: -80 }}
    transition={{ duration: 0.3, ease: "easeOut" }}
    className="bg-[#0B0F1A] border border-primary/30 rounded-2xl p-5"
    data-testid={`set-card-${setIndex}`}
  >
    {/* Set indicator */}
    <div className="flex items-center justify-between mb-5">
      <span className="text-xs tracking-widest text-primary font-bold uppercase">Set {setIndex + 1} of {numSets}</span>
      <span className="text-xs text-white/25">{rest} min rest</span>
    </div>

    {/* Weight & Reps — large, centered */}
    <div className="text-center mb-5">
      {weight ? (
        <>
          <div className="text-5xl font-black text-white leading-none">{weight}</div>
          <div className="text-sm text-white/40 mt-1">lbs</div>
          <div className="text-xl text-primary font-bold mt-2">× {reps} rep{parseInt(reps) !== 1 ? "s" : ""}</div>
          {plates && <p className="text-xs text-accent mt-2">Each side: {plates}</p>}
          {equipLabel && <p className="text-[10px] text-white/20 mt-1">{equipLabel}</p>}
        </>
      ) : (
        <>
          <div className="text-2xl text-white/30 font-bold">—</div>
          <div className="text-xl text-primary font-bold mt-2">× {reps} rep{parseInt(reps) !== 1 ? "s" : ""}</div>
        </>
      )}
    </div>

    {/* Actual weight override — inline */}
    <div className="flex items-center gap-2 mb-5">
      <label className="text-xs text-white/30 flex-shrink-0">Actual:</label>
      <input type="number" placeholder={weight ? weight.toString() : "0"} value={actualWeight || ""}
        onChange={(e) => onActualChange(e.target.value)}
        className="flex-1 bg-[#1E293B] border border-[#2D3B4F] rounded-lg px-3 py-2 text-white text-sm focus:border-primary outline-none text-center" />
      <span className="text-xs text-white/20">lbs</span>
    </div>

    {/* DONE */}
    <button type="button" onClick={onComplete}
      className="w-full bg-primary hover:bg-primary/80 text-white font-bold py-4 rounded-xl transition-colors text-lg active:scale-[0.97]"
      data-testid={`set-complete-btn-${setIndex}`}>
      Set Complete
    </button>
  </motion.div>
);

// ─── COMPLETED SET PILL (swipe back) ───
const CompletedSetPill = ({ setIndex, weight, reps }) => (
  <div className="flex items-center gap-2 py-1 text-xs text-white/40">
    <Check className="w-3 h-3 text-success" />
    <span>Set {setIndex + 1}:</span>
    <span className="text-white/60 font-medium">{weight || "—"} lbs × {reps}</span>
  </div>
);

// ─── MAIN COMPONENT ───
const ExerciseSetTracker = ({ exercise, sectionIndex, exerciseIndex, exerciseLog, onUpdateLog, isStarted, userMax, onUpdateMax, equipmentList = [], weightIntent, onSaveWeightIntent, isDeloadMode = false, safetyConfirmed = true, allUserMaxes = {}, onSwitchExercise, progressionInfo = null, onLevelUp, videoInfo, onExerciseComplete, isStrengthWorkout = true }) => {
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [useAlt, setUseAlt] = useState(exercise._athleteUsesAlt || false);
  const [showCountdown, setShowCountdown] = useState(false);
  // The wheel always starts at "Same" (0). The athlete spins to the desired
  // delta and then taps Confirm — that's when we save & advance.
  const [pendingDelta, setPendingDelta] = useState(0);
  const [confirmed, setConfirmed] = useState(false);
  const [viewingSet, setViewingSet] = useState(null); // null = current, number = viewing old set

  const activeExercise = useAlt && exercise.alt_exercise_id ? {
    ...exercise, exercise_id: exercise.alt_exercise_id, exercise_name: exercise.alt_exercise_name,
    bar_equipment_id: exercise.alt_bar_equipment_id, sets_data: exercise.alt_sets_data || exercise.sets_data,
    sets: exercise.alt_sets_data ? exercise.alt_sets_data.length : exercise.sets,
  } : exercise;

  const numSets = activeExercise.sets || 3;
  const rawTargetReps = activeExercise.reps || "10";
  const targetReps = isDeloadMode ? Math.max(1, Math.ceil(parseInt(rawTargetReps) / 2)).toString() : rawTargetReps;
  const setsDataFromWorkout = activeExercise.sets_data || [];
  const basedOffId = exercise.based_off_exercise_id;
  const effectiveMax = basedOffId && basedOffId !== exercise.exercise_id ? (allUserMaxes[basedOffId] || "") : userMax;
  const [localMax, setLocalMax] = useState(effectiveMax || "");

  const getSetData = () => exerciseLog?.sets || Array.from({ length: numSets }, () => ({ weight: "", reps: "", completed: false }));
  const [setsData, setSetsData] = useState(getSetData);

  const currentSetIndex = setsData.findIndex(s => !s.completed);
  const allComplete = currentSetIndex === -1;
  const completedSets = setsData.filter(s => s.completed).length;

  useEffect(() => {
    const max = basedOffId && basedOffId !== exercise.exercise_id ? (allUserMaxes[basedOffId] || "") : userMax;
    if (max) setLocalMax(max.toString());
  }, [userMax, allUserMaxes, basedOffId, exercise.exercise_id]);

  useEffect(() => {
    if (isStarted) {
      onUpdateLog(sectionIndex, exerciseIndex, {
        exercise_id: exercise.exercise_id, exercise_name: exercise.exercise_name || "Exercise",
        sets: setsData, completed: allComplete
      });
    }
  }, [setsData, isStarted]); // eslint-disable-line react-hooks/exhaustive-deps

  // Equipment
  const getMatchedEquipment = () => {
    if (activeExercise.bar_equipment_id) { const m = equipmentList.find(eq => eq.equipment_id === activeExercise.bar_equipment_id); if (m) return m; }
    const name = (activeExercise.exercise_name || "").toLowerCase();
    const sorted = [...equipmentList].sort((a, b) => (b.name || "").length - (a.name || "").length);
    for (const eq of sorted) { if (eq.name && name.includes(eq.name.toLowerCase())) return eq; }
    return null;
  };
  const matchedEquipment = getMatchedEquipment();
  const barWeight = matchedEquipment ? matchedEquipment.weight : null;

  const roundTo5 = w => Math.round(w / 5) * 5;
  const PLATES = [45, 35, 25, 10, 5, 2.5];

  const calcWeight = (i) => {
    const cfg = setsDataFromWorkout[i];
    if (!cfg?.percent_max || !localMax) return null;
    const pct = parseFloat(cfg.percent_max.replace("%", "").trim());
    const max = parseFloat(localMax);
    if (isNaN(pct) || pct <= 0 || isNaN(max) || max <= 0) return null;
    return roundTo5(max * (pct / 100));
  };

  const calcPlates = (total) => {
    if (!barWeight || !total) return null;
    let perSide = Math.round(((total - barWeight) / 2) / 2.5) * 2.5;
    if (perSide <= 0) return null;
    const plates = []; let rem = perSide;
    for (const pw of PLATES) { const c = Math.floor(rem / pw); if (c > 0) { plates.push(`${pw}s×${c}`); rem -= c * pw; } }
    return plates.join(", ");
  };

  const getReps = (i) => {
    const cfg = setsDataFromWorkout[i];
    let r = cfg?.reps || targetReps;
    if (isDeloadMode && r) { const n = parseInt(r); if (!isNaN(n)) return Math.max(1, Math.ceil(n / 2)).toString(); }
    return r;
  };

  const getRest = (i) => {
    const cfg = setsDataFromWorkout[i];
    if (cfg?.rest_mins) return cfg.rest_mins;
    return exercise.rest_seconds ? Math.round(exercise.rest_seconds / 60) : "1";
  };

  const handleMaxBlur = () => { if (localMax && onUpdateMax) onUpdateMax(exercise.exercise_id, exercise.exercise_name, parseFloat(localMax)); };

  const updateSet = (i, field, value) => {
    setSetsData(prev => { const u = [...prev]; u[i] = { ...u[i], [field]: value }; return u; });
  };

  const handleSetComplete = () => {
    if (currentSetIndex === -1) return;
    setViewingSet(null);
    setSetsData(prev => {
      const u = [...prev];
      u[currentSetIndex] = { ...u[currentSetIndex], completed: true };
      // Don't auto-countdown — wait for weight intent selection
      return u;
    });
  };

  const handleCountdownComplete = useCallback(() => {
    setShowCountdown(false);
    if (onExerciseComplete) onExerciseComplete(sectionIndex, exerciseIndex);
  }, [onExerciseComplete, sectionIndex, exerciseIndex]);

  const [customWeight, setCustomWeight] = useState("");
  const [showCustomInput, setShowCustomInput] = useState(false);

  // Whether this exercise should auto-bump the athlete's stored max when they
  // pick a non-zero delta. Limited strictly to strength workouts (per user
  // direction) and skipped for "based-off" exercises whose max is derived
  // from another lift (we don't want a single accessory bumping the parent).
  const isDerivedMax = basedOffId && basedOffId !== exercise.exercise_id;
  const canBumpMax = isStrengthWorkout && !isDerivedMax;

  const applyDelta = (delta) => {
    // Persist the chosen weight intent (kept for backward compat / coach view).
    if (onSaveWeightIntent) onSaveWeightIntent(exercise.exercise_id, delta);

    // For strength workouts, immediately update the athlete's 1RM max so the
    // next session loads heavier (or lighter) without manual editing.
    if (canBumpMax && delta !== 0 && onUpdateMax) {
      const currentMax = parseFloat(localMax);
      if (!isNaN(currentMax) && currentMax > 0) {
        const newMax = Math.max(0, currentMax + delta);
        setLocalMax(newMax.toString());
        onUpdateMax(exercise.exercise_id, exercise.exercise_name, newMax);
      }
    }
  };

  const handleConfirmDelta = () => {
    if (confirmed) return;
    setConfirmed(true);
    setShowCustomInput(false);
    applyDelta(pendingDelta);
    setTimeout(() => setShowCountdown(true), 400);
  };

  const handleCustomConfirm = () => {
    const val = parseFloat(customWeight);
    if (isNaN(val)) return;
    setPendingDelta(val);
    setConfirmed(true);
    applyDelta(val);
    setTimeout(() => setShowCountdown(true), 400);
  };


  const equipLabel = matchedEquipment ? `${matchedEquipment.name} (${matchedEquipment.weight} lbs)` : null;

  return (
    <>
      <div className={`rounded-xl overflow-hidden transition-all ${allComplete ? "bg-success/15 border border-success/40" : "bg-[#141B2D] border border-[#1E293B]"}`}
        data-testid={`exercise-tracker-${sectionIndex}-${exerciseIndex}`}>

        {/* ── HEADER ── */}
        <div className="p-3 sm:p-4">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold flex-shrink-0 text-sm ${allComplete ? "bg-success text-white" : "bg-primary/20 text-primary"}`}>
              {allComplete ? <Check className="w-5 h-5" /> : exerciseIndex + 1}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`font-semibold text-base ${allComplete ? "text-success" : "text-white"}`}>{activeExercise.exercise_name || "Exercise"}</span>
                {videoInfo && (
                  <button type="button" onClick={() => setShowVideoModal(true)}
                    className="inline-flex items-center gap-1 text-[10px] font-semibold text-blue-400 bg-blue-500/15 border border-blue-500/30 px-2 py-0.5 rounded-full"
                    data-testid={`watch-demo-${sectionIndex}-${exerciseIndex}`}><Play className="w-2.5 h-2.5" /> Demo</button>
                )}
              </div>
              {basedOffId && basedOffId !== activeExercise.exercise_id && (
                <span className="inline-flex items-center gap-1 text-xs text-cyan-400 bg-cyan-500/15 border border-cyan-500/30 px-2 py-0.5 rounded-full mt-1">
                  <Link2 className="w-3 h-3" /> Based off {exercise.based_off_exercise_name}
                </span>
              )}
              {exercise.alt_exercise_id && (
                <button type="button" onClick={() => { const n = !useAlt; setUseAlt(n); onSwitchExercise?.(exercise.exercise_id, n ? exercise.alt_exercise_id : exercise.exercise_id); }}
                  className={`text-[10px] px-2 py-0.5 rounded-full font-medium border mt-1 ${useAlt ? "bg-amber-500/20 text-amber-400 border-amber-500/30" : "bg-[#1E293B] text-gray-500 border-[#2D3B4F]"}`}
                  data-testid={`alt-toggle-${sectionIndex}-${exerciseIndex}`}>{useAlt ? `Alt: ${exercise.alt_exercise_name}` : "Switch to Alt"}</button>
              )}
            </div>
            {/* Progress dots */}
            <div className="flex gap-1.5 flex-shrink-0">
              {Array.from({ length: numSets }).map((_, i) => (
                <button key={i} onClick={() => setsData[i]?.completed && setViewingSet(viewingSet === i ? null : i)}
                  className={`w-2.5 h-2.5 rounded-full transition-all ${setsData[i]?.completed ? "bg-success cursor-pointer hover:ring-2 ring-success/40" : i === currentSetIndex ? "bg-primary animate-pulse" : "bg-[#374B69]"}`} />
              ))}
            </div>
          </div>
        </div>

        {/* ── ACTIVE WORKOUT AREA ── */}
        {isStarted && (
          <div className={`px-3 sm:px-4 pb-4 border-t border-[#1E293B]/50 pt-3 ${!safetyConfirmed ? "opacity-30 pointer-events-none" : ""}`}>

            {/* Max Input — compact */}
            {basedOffId && basedOffId !== activeExercise.exercise_id ? (
              <div className="flex items-center gap-2 p-2.5 bg-cyan-500/10 border border-cyan-500/30 rounded-lg mb-3 text-sm">
                <Link2 className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                <span className="text-cyan-400">{exercise.based_off_exercise_name} Max:</span>
                <span className="text-white font-bold">{localMax || "—"} lbs</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 p-2.5 bg-amber-500/10 border border-amber-500/30 rounded-lg mb-3">
                <Target className="w-4 h-4 text-amber-400 flex-shrink-0" />
                <span className="text-sm text-amber-400 font-medium">Max:</span>
                <input type="number" placeholder="Enter" value={localMax}
                  onChange={(e) => setLocalMax(e.target.value)} onBlur={handleMaxBlur}
                  className="flex-1 bg-[#0B0F1A] border border-amber-500/50 rounded-lg px-3 py-1.5 text-white text-sm focus:border-amber-400 outline-none max-w-[100px] text-center"
                  data-testid={`max-input-${sectionIndex}-${exerciseIndex}`} />
                <span className="text-xs text-amber-400/50">lbs</span>
              </div>
            )}

            {/* ── VIEWING OLD SET (tapped a completed dot) ── */}
            {viewingSet !== null && setsData[viewingSet]?.completed && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                className="bg-[#0B0F1A] border border-success/20 rounded-xl p-4 mb-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-success font-bold uppercase">Set {viewingSet + 1} — Completed</span>
                  <button onClick={() => setViewingSet(null)} className="text-xs text-white/30 hover:text-white/60"><ChevronLeft className="w-4 h-4 inline" /> Back to current</button>
                </div>
                <div className="text-center">
                  <span className="text-3xl font-black text-white">{calcWeight(viewingSet) || setsData[viewingSet]?.weight || "—"}</span>
                  <span className="text-sm text-white/40 ml-1">lbs</span>
                  <span className="text-lg text-primary font-bold ml-3">× {getReps(viewingSet)}</span>
                  {setsData[viewingSet]?.weight && setsData[viewingSet].weight !== (calcWeight(viewingSet) || "").toString() && (
                    <p className="text-xs text-amber-400 mt-1">Actual: {setsData[viewingSet].weight} lbs</p>
                  )}
                </div>
              </motion.div>
            )}

            {/* ── COUNTDOWN ── */}
            {showCountdown && <ExerciseCountdown onComplete={handleCountdownComplete} />}

            {/* ── CURRENT SET CARD (the swipe card) ── */}
            {!allComplete && !showCountdown && viewingSet === null && (
              <AnimatePresence mode="wait">
                <SetCard
                  key={currentSetIndex}
                  setIndex={currentSetIndex}
                  numSets={numSets}
                  weight={calcWeight(currentSetIndex)}
                  reps={getReps(currentSetIndex)}
                  plates={calcPlates(calcWeight(currentSetIndex))}
                  rest={getRest(currentSetIndex)}
                  actualWeight={setsData[currentSetIndex]?.weight}
                  onActualChange={(v) => updateSet(currentSetIndex, "weight", v)}
                  onComplete={handleSetComplete}
                  equipLabel={equipLabel}
                />
              </AnimatePresence>
            )}

            {/* ── ALL COMPLETE ── */}
            {allComplete && !showCountdown && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3">
                {/* Summary */}
                <div className="bg-success/10 border border-success/30 rounded-xl p-4 text-center">
                  <Check className="w-7 h-7 text-success mx-auto mb-1" />
                  <p className="text-success font-bold text-sm">All {numSets} sets complete!</p>
                  <div className="mt-2 space-y-0.5">
                    {setsData.map((s, i) => <CompletedSetPill key={i} setIndex={i} weight={calcWeight(i) || s.weight} reps={getReps(i)} />)}
                  </div>
                </div>

                {/* Weight intent — REQUIRED before advancing.
                    iOS-style wheel picker (5 lb increments, -20 to +20).   */}
                <div className="p-4 bg-[#0B0F1A] border border-primary/30 rounded-xl" data-testid={`weight-intent-${sectionIndex}-${exerciseIndex}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-primary" />
                      <span className="text-sm font-bold text-white">Next time, I want to:</span>
                    </div>
                    {canBumpMax && (
                      <span className="text-[10px] text-amber-400/70 uppercase tracking-wider font-bold">Auto-updates max</span>
                    )}
                  </div>
                  <p className="text-xs text-white/40 mb-3">Spin the wheel — center value is selected.</p>

                  <WheelPicker
                    stops={DELTA_STOPS}
                    value={pendingDelta}
                    onChange={setPendingDelta}
                    testIdPrefix={`delta-${sectionIndex}-${exerciseIndex}`}
                  />

                  <div className="flex items-center gap-2 mt-3">
                    <button
                      type="button"
                      onClick={handleConfirmDelta}
                      disabled={confirmed}
                      className="flex-1 bg-primary hover:bg-primary/80 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold py-2.5 rounded-lg transition-colors text-sm"
                      data-testid={`confirm-delta-${sectionIndex}-${exerciseIndex}`}>
                      {confirmed ? "Saved ✓" : `Confirm ${formatDelta(pendingDelta)}`}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowCustomInput(s => !s)}
                      className={`px-3 py-2.5 rounded-lg text-sm font-semibold border transition-all ${showCustomInput ? "bg-amber-500 text-black border-amber-500" : "bg-amber-500/10 text-amber-300 border-amber-500/30 hover:bg-amber-500/20"}`}
                      data-testid={`custom-toggle-${sectionIndex}-${exerciseIndex}`}>
                      Custom
                    </button>
                  </div>

                  {showCustomInput && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="mt-3 flex items-center gap-2">
                      <span className="text-sm text-amber-400 font-medium">Δ</span>
                      <input type="number" step="0.5" placeholder="Enter lbs (+/-)" value={customWeight}
                        onChange={(e) => setCustomWeight(e.target.value)} autoFocus
                        className="flex-1 bg-[#1E293B] border border-amber-500/50 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-400 outline-none text-center"
                        data-testid={`custom-weight-input-${sectionIndex}-${exerciseIndex}`} />
                      <span className="text-xs text-white/30">lbs</span>
                      <button type="button" onClick={handleCustomConfirm}
                        className="bg-amber-500 hover:bg-amber-400 text-black font-bold px-4 py-2 rounded-lg text-sm transition-colors"
                        data-testid={`custom-confirm-${sectionIndex}-${exerciseIndex}`}>Go</button>
                    </motion.div>
                  )}

                  {confirmed && canBumpMax && pendingDelta !== 0 && localMax && (
                    <p className="text-xs text-emerald-400 mt-2 flex items-center gap-1.5">
                      <ArrowUpCircle className="w-3.5 h-3.5" />
                      New max: <span className="font-bold">{localMax} lbs</span>
                    </p>
                  )}
                </div>

                {/* Level progression */}
                {progressionInfo && !progressionInfo.isMaxLevel && <LevelProgressionPanel progressionInfo={progressionInfo} sectionIndex={sectionIndex} exerciseIndex={exerciseIndex} onLevelUp={onLevelUp} />}
                {progressionInfo?.isMaxLevel && (
                  <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-xl flex items-center gap-2">
                    <Trophy className="w-4 h-4 text-amber-400" /><span className="text-sm font-medium text-amber-400">Max Level ({progressionInfo.currentLevel}/{progressionInfo.totalLevels})</span>
                  </div>
                )}
              </motion.div>
            )}
          </div>
        )}
      </div>

      {/* Video Modal */}
      <AnimatePresence>
        {showVideoModal && videoInfo && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4" onClick={() => setShowVideoModal(false)}>
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }}
              className="bg-[#111827] border border-[#2D3B4F] rounded-2xl overflow-hidden max-w-2xl w-full" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between p-3 border-b border-[#1E293B]">
                <h4 className="text-sm font-bold text-white">{videoInfo.title}</h4>
                <button onClick={() => setShowVideoModal(false)} className="text-gray-500 hover:text-white p-1"><X className="w-5 h-5" /></button>
              </div>
              <div className="aspect-video">
                <iframe src={`https://www.youtube.com/embed/${videoInfo.youtube_id}?autoplay=1`} title={videoInfo.title}
                  className="w-full h-full" allowFullScreen allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export { ExerciseSetTracker };
